import * as Sentry from '@sentry/nextjs';
import {
  GetStaticPaths,
  GetStaticPropsContext,
  InferGetStaticPropsType,
  NextPage,
} from 'next';

import { addLocalePrefix } from 'utils/routes.utils';

import { Duration } from 'constants/duration.constants';
import { NOT_FOUND } from 'constants/nextjs.constants';
import { Routes } from 'constants/routes.constants';
import { pageEventQuery } from 'queries/events/queries';
import {
  ticketsEventIdByDatoEventSlugQuery,
  ticketsEventQuery,
  ticketsEventTicketQuery,
  ticketsInfoQuery,
} from 'queries/tickets/queries';
import Prefetcher from 'services/prefetcher';

import EventTicketInquiryContainer from 'components/@events/EventTicketInquiryContainer';
import { isEventTicketingEnabled, isPastEvent } from 'components/@events/utils';
import PageSeo from 'components/@seo/PageSeo';

const EventInquiry: NextPage<
  InferGetStaticPropsType<typeof getStaticProps>
> = ({ event, ticket }) => {
  return (
    <>
      <PageSeo tags={event.seoTags} nofollow noindex />
      <EventTicketInquiryContainer event={event} ticket={ticket} />
    </>
  );
};

export default EventInquiry;

// We explicitly don't want to generate pages at build time because we use redirects in getStaticProps.
// stackoverflow.com/questions/66886040/trying-to-redirect-from-getstaticprops-returns-error-during-build
export const getStaticPaths: GetStaticPaths = () => {
  return { paths: [], fallback: 'blocking' };
};

export const getStaticProps = async ({
  locale,
  params,
  preview,
}: GetStaticPropsContext<{ slug: string; id: string }>) => {
  const prefetcher = await Prefetcher.init({ locale, preview });
  const slug = params?.slug;
  const ticketId = params?.id;

  if (!slug || !ticketId) return { ...NOT_FOUND };

  const [{ pageEvent }] = await Promise.all([
    prefetcher.prefetch(
      pageEventQuery,
      { filter: { slug: { eq: slug } } },
      { meta: { dehydrate: true } },
    ),
    prefetcher.prefetch(
      ticketsEventIdByDatoEventSlugQuery,
      { slug },
      { meta: { dehydrate: true } },
    ),
  ]);

  if (!pageEvent) return { ...NOT_FOUND };

  const pastEvent = isPastEvent(pageEvent);

  if (!isEventTicketingEnabled(pageEvent) || pastEvent)
    return {
      redirect: {
        destination: addLocalePrefix(locale, Routes.EventDetail, { slug }),
        permanent: false,
      },
      revalidate: Duration.TEN_MIN,
    };

  const eventId = pageEvent.ticketsEventId;

  // In case something is off with ticketing, redirect to the event page
  // This way we don't show a 500 error to the user
  try {
    const [{ event }, { ticket }] = await Promise.all([
      prefetcher.prefetch(
        ticketsEventQuery,
        { id: eventId, timeslotId: pageEvent.ticketsTimeslotId },
        { meta: { dehydrate: true } },
      ),
      prefetcher.prefetch(ticketsEventTicketQuery, {
        eventId,
        ticketId,
      }),
    ]);

    if (!event || !ticket || !ticket.isExternalForm) return { ...NOT_FOUND };

    await prefetcher.prefetch(
      ticketsInfoQuery,
      { eventId, ticketIds: [ticketId] },
      { meta: { dehydrate: true } },
    );

    return {
      props: {
        event: pageEvent,
        ticket: ticket,
        dehydratedState: prefetcher.dehydratedState,
      },
      revalidate: Duration.TEN_MIN,
    };
  } catch (error) {
    console.error(error);
    Sentry.captureException(error);

    return {
      redirect: {
        destination: addLocalePrefix(locale, Routes.EventDetail, { slug }),
        permanent: false,
      },
      revalidate: Duration.TEN_MIN,
    };
  }
};
